"use client"

import { Wrench } from "lucide-react"
import { CATEGORY_LABEL } from "@/lib/community/showcase-constants"
import type { CommunityPost } from "@/lib/community/types"

type Tool = NonNullable<CommunityPost["toolDetail"]>["tools"][number]

interface ToolShowcaseProps {
  toolDetail: NonNullable<CommunityPost["toolDetail"]>
}

export function ToolShowcase({ toolDetail }: ToolShowcaseProps) {
  const { tools } = toolDetail
  if (tools.length === 0) return null

  // Group tools by agent
  const grouped = tools.reduce<Record<string, { agentName: string; tools: Tool[] }>>(
    (acc, tool) => {
      if (!acc[tool.agentId]) {
        acc[tool.agentId] = { agentName: tool.agentName, tools: [] }
      }
      acc[tool.agentId].tools.push(tool)
      return acc
    },
    {}
  )

  return (
    <div className="mb-5 space-y-2 w-fit">
      <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
        <Wrench className="h-3.5 w-3.5 text-amber-500" />
        工具详情
      </h4>

      <div className="rounded-xl border border-amber-500/15 bg-amber-500/[0.03] overflow-hidden">
        {Object.entries(grouped).map(([agentId, group], groupIdx) => (
          <div
            key={agentId}
            className={groupIdx > 0 ? "border-t border-amber-500/10" : ""}
          >
            {/* Agent header */}
            <div className="px-3 py-1.5 flex items-center gap-1.5">
              <span className="text-caption font-semibold text-foreground/80">
                {group.agentName}
              </span>
              <span className="text-micro text-muted-foreground">
                {group.tools.length} 个工具
              </span>
            </div>

            {/* Tool list */}
            <div className="px-3 pb-2 space-y-1">
              {group.tools.map((tool) => (
                <div
                  key={tool.id}
                  className="flex items-center gap-2 rounded-md px-2.5 py-1.5 bg-secondary/15"
                >
                  <span className="text-caption font-medium text-foreground truncate flex-1">
                    {tool.label}
                  </span>
                  <span className="text-nano bg-secondary/50 text-muted-foreground px-1 rounded shrink-0">
                    {CATEGORY_LABEL[tool.category] ?? tool.category}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
